import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home, MapPin, DollarSign, Bed, Bath, Maximize2, Star,
  Zap, CheckCircle, X, ChevronRight, ChevronLeft, Leaf, Droplets,
  AlertTriangle, Heart, RefreshCw,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Canonical Vocabulary ──────────────────────────────────────────────────────
const PROPERTY_TYPES = [
  { id: 'single_family', label: 'Single Family', icon: '🏠' },
  { id: 'condo',         label: 'Condo',         icon: '🏢' },
  { id: 'townhouse',     label: 'Townhouse',      icon: '🏘️' },
  { id: 'multi_family',  label: 'Multi-Family',   icon: '🏗️' },
  { id: 'land',          label: 'Land',           icon: '🌿' },
  { id: 'farm_ranch',    label: 'Farm / Ranch',   icon: '🐴' },
  { id: 'villa',         label: 'Villa',          icon: '🏰' },
];
const FEATURES = [
  { id: 'pool',          label: 'Pool' },
  { id: 'garage',        label: 'Garage' },
  { id: 'adu',           label: 'ADU / Casita' },
  { id: 'office',        label: 'Home Office' },
  { id: 'rv_parking',    label: 'RV Parking' },
  { id: 'shop_barn',     label: 'Shop / Barn' },
  { id: 'solar',         label: 'Solar Panels' },
  { id: 'ev_charger',    label: 'EV Charger' },
  { id: 'fireplace',     label: 'Fireplace' },
  { id: 'gated',         label: 'Gated Entry' },
  { id: 'guest_house',   label: 'Guest House' },
  { id: 'gym',           label: 'Home Gym' },
  { id: 'no_hoa',        label: 'No HOA' },
];
const LIFESTYLE_TAGS = [
  { id: 'private',       label: 'Private & Quiet', icon: '🌲' },
  { id: 'entertainer',   label: 'Entertainer',     icon: '🥂' },
  { id: 'family',        label: 'Family-Oriented',  icon: '👨‍👩‍👧' },
  { id: 'off_grid',      label: 'Off-Grid',        icon: '⚡' },
  { id: 'luxury',        label: 'Luxury',          icon: '✨' },
  { id: 'investment',    label: 'Investment',      icon: '📈' },
  { id: 'equestrian',    label: 'Equestrian',      icon: '🐎' },
  { id: 'walkable',      label: 'Walkable Area',   icon: '🚶' },
  { id: 'resort',        label: 'Resort-Style',    icon: '🏖️' },
  { id: 'agricultural',  label: 'Agricultural',    icon: '🌾' },
];
const NEGATIVE_FLAGS = [
  { id: 'hoa',           label: 'HOA Required' },
  { id: 'busy_road',     label: 'Busy Road' },
  { id: 'fire_zone',     label: 'High Fire Zone' },
  { id: 'flood_zone',    label: 'Flood Zone' },
  { id: 'fixer',         label: 'Fixer-Upper' },
  { id: 'shared_driveway', label: 'Shared Driveway' },
  { id: 'easements',     label: 'Easements' },
  { id: 'power_lines',   label: 'Power Lines' },
  { id: 'near_commercial', label: 'Near Commercial' },
];
const ZONING_TYPES = [
  { id: 'residential',   label: 'Residential' },
  { id: 'agricultural',  label: 'Agricultural' },
  { id: 'rural',         label: 'Rural' },
  { id: 'mixed',         label: 'Mixed Use' },
  { id: 'pud',           label: 'PUD' },
];

const BEDS  = [1, 2, 3, 4, 5, '6+'];
const BATHS = [1, 1.5, 2, 2.5, 3, '3.5+'];
const TOTAL_STEPS = 11;

const INIT = {
  intent:               'buy',
  location: {
    areas: [],          // [{name, type, country_code, state_province}] — canonical v3
    cities: [],         // legacy fallback
    radius_km: 40,      // canonical unit (display: mi or km per units_preference)
    units_preference: 'imperial',
  },
  budget:               { min_price: '', max_price: '', flexibility_pct: 5, not_sure: false },
  core_attributes:      { property_types: [], min_beds: 3, min_baths: 2, min_sqft: '', max_sqft: '' },
  feature_flags:        { must_have: [], nice_to_have: [] },
  land_attributes:      { min_usable_land_percent: '', zoning_types: [], horse_property: false },
  utilities:            { solar_preference: 'any', sewer_type: 'any', internet_type: 'any' },
  financial_attributes: { max_hoa: '', str_allowed_required: false, income_potential_required: false },
  lifestyle_tags:       [],
  negative_flags:       [],
  // v3 canonical (populated via RefinementPanel — not shown in initial wizard)
  amenities:            { location: [], community: [], property: [], land: [], utilities: [], special: [] },
  style:                [],
  kitchen:              { layout: [], island: [], countertops: [], appliances: [], features: [] },
  furnishing:           { status: '', includes: [] },
};

// ─── Pill / Toggle helpers ─────────────────────────────────────────────────────
const ACCENT = '#0A192F';
const GREEN  = '#10B981';

function Pill({ active, onClick, children, color }) {
  return (
    <button type="button" onClick={onClick}
      className="px-3 py-2 rounded-full text-xs font-semibold border transition-all duration-150"
      style={active
        ? { background: color || ACCENT, color: 'white', borderColor: color || ACCENT }
        : { background: 'white', color: '#475569', borderColor: '#E2E8F0' }}>
      {children}
    </button>
  );
}

function NumberPill({ value, active, onClick }) {
  return (
    <button type="button" onClick={onClick}
      className="w-11 h-11 rounded-xl text-sm font-bold transition-all duration-150 flex items-center justify-center"
      style={active
        ? { background: ACCENT, color: 'white' }
        : { background: '#F1F5F9', color: '#475569' }}>
      {value}
    </button>
  );
}

function FeatureToggle({ feature, state, onToggle }) {
  // state: '' | 'nice' | 'must'
  const colors = { '': { bg: '#F8FAFC', text: '#94A3B8', border: '#E2E8F0' },
    nice: { bg: '#FFFBEB', text: '#D97706', border: '#FDE68A' },
    must: { bg: '#F0FDF4', text: '#16A34A', border: '#86EFAC' } };
  const c = colors[state] || colors[''];
  return (
    <button type="button" onClick={onToggle}
      className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all border"
      style={{ background: c.bg, color: c.text, borderColor: c.border }}>
      {state === 'must' && <CheckCircle size={11} />}
      {state === 'nice' && <Star size={11} />}
      {feature.label}
    </button>
  );
}

// ─── Step Components ───────────────────────────────────────────────────────────
function StepIntent({ w, setW }) {
  const opts = [
    { id: 'buy',         label: 'Buy a Home',      sub: "I'm ready to purchase",       icon: Home },
    { id: 'invest',      label: 'Invest',          sub: 'Looking for rental income',   icon: Star },
    { id: 'second_home', label: 'Second Home',     sub: 'Vacation or part-time use',  icon: Heart },
    { id: 'browsing',    label: 'Just Browsing',   sub: "Exploring what's out there",  icon: RefreshCw },
  ];
  return (
    <div>
      <StepHeader step={1} title="What's your intent?" sub="This helps us personalize your feed." />
      <div className="grid grid-cols-2 gap-3">
        {opts.map(o => (
          <button key={o.id} type="button" onClick={() => setW('intent', o.id)}
            className="p-4 rounded-2xl border-2 text-left transition-all"
            style={w.intent === o.id
              ? { borderColor: ACCENT, background: '#F0F7FF' }
              : { borderColor: '#E2E8F0', background: 'white' }}>
            <o.icon size={20} className="mb-2" style={{ color: w.intent === o.id ? ACCENT : '#94A3B8' }} />
            <div className="font-bold text-sm" style={{ color: ACCENT }}>{o.label}</div>
            <div className="text-xs text-slate-400 mt-0.5">{o.sub}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

const AREA_TYPES = [
  { id: 'city',         label: 'City' },
  { id: 'neighborhood', label: 'Neighborhood' },
  { id: 'town',         label: 'Town' },
  { id: 'village',      label: 'Village' },
  { id: 'enclave',      label: 'Enclave' },
  { id: 'district',     label: 'District' },
  { id: 'region',       label: 'Region' },
];

function StepLocation({ w, setW }) {
  const [areaInput, setAreaInput] = useState('');
  const [areaType,  setAreaType]  = useState('city');
  const loc   = w.location || {};
  const areas = loc.areas || [];
  const units = loc.units_preference || 'imperial';

  // Radius in canonical km; display in mi or km
  const radiusKm   = loc.radius_km || 40;
  const toDisplay  = (km) => units === 'imperial' ? Math.round(km * 0.621371) : km;
  const fromDisplay = (v) => units === 'imperial' ? Math.round(v / 0.621371) : v;
  const displayRadius = toDisplay(radiusKm);
  const radiusMax  = units === 'imperial' ? 100 : 160;
  const radiusStep = units === 'imperial' ? 5 : 8;
  const unitLabel  = units === 'imperial' ? 'mi' : 'km';

  const addArea = () => {
    const name = areaInput.trim();
    if (!name) return;
    if (areas.some(a => a.name.toLowerCase() === name.toLowerCase())) { setAreaInput(''); return; }
    const newArea = { name, type: areaType, country_code: 'US', state_province: '' };
    setW('location', { ...loc, areas: [...areas, newArea], cities: [...(loc.cities || []), name] });
    setAreaInput('');
  };

  const removeArea = (name) => setW('location', {
    ...loc,
    areas:  areas.filter(a => a.name !== name),
    cities: (loc.cities || []).filter(c => c !== name),
  });

  const setRadius = (displayVal) =>
    setW('location', { ...loc, radius_km: fromDisplay(parseInt(displayVal)) });

  const toggleUnits = () => {
    const next = units === 'imperial' ? 'metric' : 'imperial';
    setW('location', { ...loc, units_preference: next });
  };

  return (
    <div>
      <StepHeader step={2} title="Where are you looking?"
        sub="Add cities, neighborhoods, enclaves — anywhere in the world." />

      {/* Area type selector */}
      <div className="flex gap-1.5 flex-wrap mb-3">
        {AREA_TYPES.map(t => (
          <button key={t.id} type="button" onClick={() => setAreaType(t.id)}
            className="px-2.5 py-1 rounded-lg text-xs font-semibold border transition-all"
            style={areaType === t.id
              ? { background: ACCENT, color: 'white', borderColor: ACCENT }
              : { background: 'white', color: '#64748B', borderColor: '#E2E8F0' }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Search input */}
      <div className="flex gap-2 mb-3">
        <input value={areaInput} onChange={e => setAreaInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && addArea()}
          placeholder={`Add a ${areaType} (e.g. Scottsdale, Malibu, Notting Hill)...`}
          data-testid="wizard-city-input"
          className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:border-slate-400 transition-all" />
        <button type="button" onClick={addArea}
          className="px-4 py-2.5 rounded-xl text-sm font-bold text-white"
          style={{ background: ACCENT }}>Add</button>
      </div>

      {/* Added areas */}
      {areas.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {areas.map(a => (
            <span key={a.name}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-slate-900 text-white">
              <MapPin size={10} />
              <span>{a.name}</span>
              <span className="text-white/40 text-xs">· {a.type}</span>
              <button type="button" onClick={() => removeArea(a.name)}
                className="ml-0.5 hover:text-red-300 transition-colors">
                <X size={10} />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Radius slider */}
      <div className="mt-3 pt-3 border-t border-slate-100">
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-semibold text-slate-500">
            Search Radius: <span className="font-black text-slate-800">{displayRadius} {unitLabel}</span>
          </label>
          <button type="button" onClick={toggleUnits}
            className="text-xs font-semibold text-slate-400 hover:text-slate-700 transition-colors border border-slate-200 rounded-full px-2 py-0.5">
            Switch to {units === 'imperial' ? 'km' : 'miles'}
          </button>
        </div>
        <input type="range" min={units === 'imperial' ? 5 : 8} max={radiusMax} step={radiusStep}
          value={displayRadius}
          onChange={e => setRadius(e.target.value)}
          className="w-full accent-slate-900"
          data-testid="radius-slider" />
        <div className="flex justify-between text-xs text-slate-400 mt-1">
          <span>{units === 'imperial' ? '5 mi' : '8 km'}</span>
          <span>{units === 'imperial' ? '50 mi' : '80 km'}</span>
          <span>{units === 'imperial' ? '100 mi' : '160 km'}</span>
        </div>
      </div>
    </div>
  );
}

function StepBudget({ w, setW }) {
  const b = w.budget || {};
  const set = (k, v) => setW('budget', { ...b, [k]: v });
  return (
    <div>
      <StepHeader step={3} title="What's your budget?" sub="Set your comfortable range." />
      <div className="flex items-center gap-2 mb-5">
        <input type="checkbox" id="not_sure" checked={!!b.not_sure} onChange={e => set('not_sure', e.target.checked)}
          className="w-4 h-4 rounded" />
        <label htmlFor="not_sure" className="text-sm text-slate-600">I'm not sure yet — show me everything</label>
      </div>
      {!b.not_sure && (
        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-slate-500 mb-1.5 block">Minimum Price</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
              <input type="number" value={b.min_price || ''} onChange={e => set('min_price', e.target.value)}
                placeholder="e.g. 400,000" data-testid="wizard-min-price"
                className="w-full pl-7 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:border-slate-400 transition-all" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 mb-1.5 block">Maximum Price</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
              <input type="number" value={b.max_price || ''} onChange={e => set('max_price', e.target.value)}
                placeholder="e.g. 750,000" data-testid="wizard-max-price"
                className="w-full pl-7 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:border-slate-400 transition-all" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 mb-2 block">
              Budget Flexibility: <span className="font-bold text-slate-800">{b.flexibility_pct || 5}%</span>
            </label>
            <input type="range" min={0} max={20} step={5} value={b.flexibility_pct || 5}
              onChange={e => set('flexibility_pct', parseInt(e.target.value))}
              className="w-full accent-slate-900" />
            <div className="flex justify-between text-xs text-slate-400 mt-1">
              <span>Strict</span><span>±5%</span><span>±10%</span><span>±15%</span><span>Flexible ±20%</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StepCoreAttributes({ w, setW }) {
  const ca = w.core_attributes || {};
  const set = (k, v) => setW('core_attributes', { ...ca, [k]: v });
  const toggleType = (t) => {
    const cur = ca.property_types || [];
    set('property_types', cur.includes(t) ? cur.filter(x => x !== t) : [...cur, t]);
  };
  return (
    <div>
      <StepHeader step={4} title="What kind of property?" sub="Select all that interest you." />
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-6">
        {PROPERTY_TYPES.map(pt => {
          const active = (ca.property_types || []).includes(pt.id);
          return (
            <button key={pt.id} type="button" onClick={() => toggleType(pt.id)}
              data-testid={`prop-type-${pt.id}`}
              className="p-3 rounded-xl border-2 text-center transition-all"
              style={active
                ? { borderColor: ACCENT, background: '#F0F7FF' }
                : { borderColor: '#E2E8F0', background: 'white' }}>
              <div className="text-lg mb-1">{pt.icon}</div>
              <div className="text-xs font-semibold" style={{ color: active ? ACCENT : '#64748B' }}>{pt.label}</div>
            </button>
          );
        })}
      </div>
      <div className="space-y-4">
        <div>
          <label className="text-xs font-semibold text-slate-500 mb-2 block">
            <Bed size={12} className="inline mr-1" />Minimum Bedrooms
          </label>
          <div className="flex gap-2 flex-wrap">
            {BEDS.map(b => (
              <NumberPill key={b} value={b} active={ca.min_beds === b || (b === '6+' && ca.min_beds >= 6)}
                onClick={() => set('min_beds', b === '6+' ? 6 : b)} />
            ))}
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500 mb-2 block">
            <Bath size={12} className="inline mr-1" />Minimum Bathrooms
          </label>
          <div className="flex gap-2 flex-wrap">
            {BATHS.map(b => (
              <NumberPill key={b} value={b} active={ca.min_baths === b || (b === '3.5+' && ca.min_baths >= 3.5)}
                onClick={() => set('min_baths', b === '3.5+' ? 3.5 : b)} />
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-slate-500 mb-1.5 block">
              <Maximize2 size={11} className="inline mr-1" />Min Sqft
            </label>
            <input type="number" value={ca.min_sqft || ''} onChange={e => set('min_sqft', e.target.value)}
              placeholder="e.g. 2,000" data-testid="wizard-min-sqft"
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:border-slate-400 transition-all" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 mb-1.5 block">Max Sqft (optional)</label>
            <input type="number" value={ca.max_sqft || ''} onChange={e => set('max_sqft', e.target.value)}
              placeholder="No limit"
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:border-slate-400 transition-all" />
          </div>
        </div>
      </div>
    </div>
  );
}

function StepFeatures({ w, setW }) {
  const ff = w.feature_flags || {};
  const must     = ff.must_have    || [];
  const nice     = ff.nice_to_have || [];

  const toggle = (id) => {
    if (must.includes(id)) {
      // must → deselect
      setW('feature_flags', { must_have: must.filter(x => x !== id), nice_to_have: nice });
    } else if (nice.includes(id)) {
      // nice → must
      setW('feature_flags', { must_have: [...must, id], nice_to_have: nice.filter(x => x !== id) });
    } else {
      // unselected → nice
      setW('feature_flags', { must_have: must, nice_to_have: [...nice, id] });
    }
  };
  const getState = (id) => must.includes(id) ? 'must' : nice.includes(id) ? 'nice' : '';

  return (
    <div>
      <StepHeader step={5} title="Which features matter?" sub="Tap once = Nice to Have. Tap again = Must Have. Tap a third time to remove." />
      <div className="flex items-center gap-3 mb-4 text-xs text-slate-500">
        <span className="flex items-center gap-1 px-2 py-1 rounded-lg bg-yellow-50 text-yellow-700 border border-yellow-200">
          <Star size={10} />Nice to Have
        </span>
        <span className="flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200">
          <CheckCircle size={10} />Must Have
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        {FEATURES.map(f => (
          <FeatureToggle key={f.id} feature={f} state={getState(f.id)} onToggle={() => toggle(f.id)} />
        ))}
      </div>
      {must.length > 0 && (
        <div className="mt-4 p-3 rounded-xl bg-emerald-50 border border-emerald-200">
          <p className="text-xs font-semibold text-emerald-700">
            Must-Haves ({must.length}): {must.map(m => FEATURES.find(f => f.id === m)?.label).join(', ')}
          </p>
        </div>
      )}
    </div>
  );
}

function StepLand({ w, setW }) {
  const la = w.land_attributes || {};
  const set = (k, v) => setW('land_attributes', { ...la, [k]: v });
  const toggleZone = (z) => {
    const cur = la.zoning_types || [];
    set('zoning_types', cur.includes(z) ? cur.filter(x => x !== z) : [...cur, z]);
  };
  return (
    <div>
      <StepHeader step={6} title="Land preferences" sub="Skip any fields that don't apply to you." />
      <div className="space-y-5">
        <div>
          <label className="text-xs font-semibold text-slate-500 mb-1.5 block">
            Minimum Usable Land (%)
          </label>
          <div className="flex gap-2 flex-wrap">
            {['Skip', '25', '50', '75', '90'].map(v => (
              <Pill key={v} active={v === 'Skip' ? !la.min_usable_land_percent : la.min_usable_land_percent === parseInt(v)}
                onClick={() => set('min_usable_land_percent', v === 'Skip' ? '' : parseInt(v))}>
                {v === 'Skip' ? 'Any' : `${v}%+`}
              </Pill>
            ))}
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500 mb-2 block">Zoning Types (optional)</label>
          <div className="flex flex-wrap gap-2">
            {ZONING_TYPES.map(z => (
              <Pill key={z.id} active={(la.zoning_types || []).includes(z.id)}
                onClick={() => toggleZone(z.id)}>{z.label}</Pill>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-between py-3 border border-slate-200 rounded-xl px-4">
          <div>
            <div className="text-sm font-semibold text-slate-700">Horse Property</div>
            <div className="text-xs text-slate-400">Requires equestrian-suitable land</div>
          </div>
          <button type="button" onClick={() => set('horse_property', !la.horse_property)}
            className="w-10 h-6 rounded-full transition-all flex items-center px-1"
            style={{ background: la.horse_property ? ACCENT : '#E2E8F0' }}>
            <div className="w-4 h-4 rounded-full bg-white shadow transition-transform"
              style={{ transform: la.horse_property ? 'translateX(16px)' : 'translateX(0)' }} />
          </button>
        </div>
      </div>
    </div>
  );
}

function StepUtilities({ w, setW }) {
  const u = w.utilities || {};
  const set = (k, v) => setW('utilities', { ...u, [k]: v });
  return (
    <div>
      <StepHeader step={7} title="Utility preferences" sub="These help narrow your matches. Any = no preference." />
      <div className="space-y-5">
        {[
          { key: 'solar_preference', label: 'Solar', icon: Zap,
            opts: [{ v: 'any', l: 'Any' }, { v: 'owned', l: 'Owned' }, { v: 'leased', l: 'Leased' }, { v: 'none', l: 'None' }] },
          { key: 'sewer_type', label: 'Sewer', icon: Droplets,
            opts: [{ v: 'any', l: 'Any' }, { v: 'city', l: 'City Sewer' }, { v: 'septic', l: 'Septic' }] },
          { key: 'internet_type', label: 'Internet', icon: Zap,
            opts: [{ v: 'any', l: 'Any' }, { v: 'fiber', l: 'Fiber' }, { v: 'cable', l: 'Cable' }, { v: 'satellite', l: 'Satellite' }] },
        ].map(({ key, label, icon: Icon, opts }) => (
          <div key={key}>
            <label className="text-xs font-semibold text-slate-500 mb-2 flex items-center gap-1">
              <Icon size={11} />{label}
            </label>
            <div className="flex flex-wrap gap-2">
              {opts.map(o => (
                <Pill key={o.v} active={(u[key] || 'any') === o.v}
                  onClick={() => set(key, o.v)}>{o.l}</Pill>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StepFinancial({ w, setW }) {
  const fa = w.financial_attributes || {};
  const set = (k, v) => setW('financial_attributes', { ...fa, [k]: v });
  return (
    <div>
      <StepHeader step={8} title="Financial constraints" sub="Set any hard limits on monthly costs." />
      <div className="space-y-5">
        <div>
          <label className="text-xs font-semibold text-slate-500 mb-2 block">Maximum HOA per month</label>
          <div className="flex gap-2 flex-wrap">
            {['No limit', '$0', '$100', '$200', '$300', '$500'].map(v => (
              <Pill key={v} active={v === 'No limit' ? fa.max_hoa === '' || fa.max_hoa == null : fa.max_hoa === parseInt(v.replace('$', ''))}
                onClick={() => set('max_hoa', v === 'No limit' ? '' : parseInt(v.replace('$', '')))}>
                {v}
              </Pill>
            ))}
          </div>
          <div className="mt-2">
            <input type="number" placeholder="Or enter custom max ($)" value={fa.max_hoa || ''}
              onChange={e => set('max_hoa', e.target.value ? parseInt(e.target.value) : '')}
              className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:border-slate-400 transition-all" />
          </div>
        </div>
        <div className="flex items-center justify-between py-3 border border-slate-200 rounded-xl px-4">
          <div>
            <div className="text-sm font-semibold text-slate-700">STR (Short-Term Rental) Allowed</div>
            <div className="text-xs text-slate-400">Required if you plan to rent on Airbnb/VRBO</div>
          </div>
          <button type="button" onClick={() => set('str_allowed_required', !fa.str_allowed_required)}
            className="w-10 h-6 rounded-full transition-all flex items-center px-1"
            style={{ background: fa.str_allowed_required ? ACCENT : '#E2E8F0' }}>
            <div className="w-4 h-4 rounded-full bg-white shadow transition-transform"
              style={{ transform: fa.str_allowed_required ? 'translateX(16px)' : 'translateX(0)' }} />
          </button>
        </div>
        <div className="flex items-center justify-between py-3 border border-slate-200 rounded-xl px-4">
          <div>
            <div className="text-sm font-semibold text-slate-700">Income Potential Required</div>
            <div className="text-xs text-slate-400">ADU, guest house, or rental unit needed</div>
          </div>
          <button type="button" onClick={() => set('income_potential_required', !fa.income_potential_required)}
            className="w-10 h-6 rounded-full transition-all flex items-center px-1"
            style={{ background: fa.income_potential_required ? ACCENT : '#E2E8F0' }}>
            <div className="w-4 h-4 rounded-full bg-white shadow transition-transform"
              style={{ transform: fa.income_potential_required ? 'translateX(16px)' : 'translateX(0)' }} />
          </button>
        </div>
      </div>
    </div>
  );
}

function StepLifestyle({ w, setW }) {
  const tags = w.lifestyle_tags || [];
  const toggle = (id) => {
    setW('lifestyle_tags', tags.includes(id) ? tags.filter(t => t !== id) : [...tags, id]);
  };
  return (
    <div>
      <StepHeader step={9} title="Your lifestyle" sub="Pick up to 5. First chosen = highest priority." />
      <div className="grid grid-cols-2 gap-2">
        {LIFESTYLE_TAGS.map((t, i) => {
          const active = tags.includes(t.id);
          const rank = tags.indexOf(t.id) + 1;
          return (
            <button key={t.id} type="button" onClick={() => toggle(t.id)}
              disabled={!active && tags.length >= 5}
              data-testid={`lifestyle-${t.id}`}
              className="flex items-center gap-2.5 p-3 rounded-xl border-2 transition-all text-left"
              style={active
                ? { borderColor: ACCENT, background: '#F0F7FF' }
                : { borderColor: '#E2E8F0', background: tags.length >= 5 ? '#F8FAFC' : 'white' }}>
              <span className="text-base">{t.icon}</span>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold" style={{ color: active ? ACCENT : '#475569' }}>{t.label}</div>
              </div>
              {active && (
                <div className="w-5 h-5 rounded-full text-white text-xs font-black flex items-center justify-center flex-shrink-0"
                  style={{ background: ACCENT }}>{rank}</div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StepDealbreakers({ w, setW }) {
  const flags = w.negative_flags || [];
  const toggle = (id) => {
    setW('negative_flags', flags.includes(id) ? flags.filter(f => f !== id) : [...flags, id]);
  };
  return (
    <div>
      <StepHeader step={10} title="Dealbreakers" sub="We'll never show you homes with these conditions." />
      <div className="p-3 mb-4 rounded-xl bg-red-50 border border-red-100">
        <p className="text-xs text-red-600 font-medium flex items-center gap-2">
          <AlertTriangle size={13} />These are hard filters — listings with selected flags are excluded from your feed.
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        {NEGATIVE_FLAGS.map(f => {
          const active = flags.includes(f.id);
          return (
            <button key={f.id} type="button" onClick={() => toggle(f.id)}
              data-testid={`dealbreaker-${f.id}`}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all border"
              style={active
                ? { background: '#FEF2F2', color: '#DC2626', borderColor: '#FECACA' }
                : { background: 'white', color: '#475569', borderColor: '#E2E8F0' }}>
              {active && <X size={11} />}
              {f.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StepReview({ w }) {
  const loc = w.location || {};
  const b = w.budget || {};
  const ca = w.core_attributes || {};
  const ff = w.feature_flags || {};
  const rows = [
    { label: 'Intent', value: w.intent?.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase()) },
    { label: 'Locations', value: (loc.cities || []).join(', ') || 'Not set' },
    { label: 'Budget', value: b.not_sure ? 'Open' : `$${parseInt(b.min_price||0).toLocaleString()} – $${parseInt(b.max_price||0).toLocaleString()}` },
    { label: 'Property Types', value: (ca.property_types || []).join(', ') || 'Any' },
    { label: 'Min Beds', value: ca.min_beds || '—' },
    { label: 'Min Baths', value: ca.min_baths || '—' },
    { label: 'Min Sqft', value: ca.min_sqft ? `${parseInt(ca.min_sqft).toLocaleString()} sqft` : 'Any' },
    { label: 'Must-Haves', value: (ff.must_have || []).join(', ') || 'None' },
    { label: 'Lifestyle', value: (w.lifestyle_tags || []).join(', ') || 'Not set' },
    { label: 'Dealbreakers', value: (w.negative_flags || []).join(', ') || 'None' },
  ];
  return (
    <div>
      <StepHeader step={11} title="Review your preferences" sub="Everything looks good? Hit Save to activate your match feed." />
      <div className="space-y-2">
        {rows.map(r => (
          <div key={r.label} className="flex items-start gap-3 py-2 border-b border-slate-100 last:border-0">
            <span className="text-xs font-bold text-slate-400 w-24 flex-shrink-0 pt-0.5">{r.label}</span>
            <span className="text-xs text-slate-700 flex-1">{r.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Step Header ──────────────────────────────────────────────────────────────
function StepHeader({ step, title, sub }) {
  return (
    <div className="mb-6">
      <div className="text-xs font-bold text-slate-400 mb-1">STEP {step} OF {TOTAL_STEPS}</div>
      <h2 className="text-xl font-black mb-1" style={{ color: ACCENT }}>{title}</h2>
      {sub && <p className="text-sm text-slate-500">{sub}</p>}
    </div>
  );
}

const STEPS = [
  StepIntent, StepLocation, StepBudget, StepCoreAttributes, StepFeatures,
  StepLand, StepUtilities, StepFinancial, StepLifestyle, StepDealbreakers, StepReview,
];

// ─── Main Wizard ──────────────────────────────────────────────────────────────
export default function BuyerIntakeWizard({ initialData, onComplete }) {
  const { authFetch } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [w, setWizState] = useState({ ...INIT, ...(initialData || {}) });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const setW = useCallback((key, val) => {
    setWizState(prev => ({ ...prev, [key]: val }));
  }, []);

  const goNext = () => { if (step < TOTAL_STEPS) setStep(s => s + 1); };
  const goBack = () => { if (step > 1) setStep(s => s - 1); };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const body = {
        ...w,
        schema_version: 2,
        budget: {
          ...w.budget,
          min_price: w.budget.min_price ? parseInt(w.budget.min_price) : null,
          max_price: w.budget.max_price ? parseInt(w.budget.max_price) : null,
        },
        core_attributes: {
          ...w.core_attributes,
          min_sqft: w.core_attributes.min_sqft ? parseInt(w.core_attributes.min_sqft) : null,
          max_sqft: w.core_attributes.max_sqft ? parseInt(w.core_attributes.max_sqft) : null,
        },
        financial_attributes: {
          ...w.financial_attributes,
          max_hoa: w.financial_attributes.max_hoa !== '' ? parseInt(w.financial_attributes.max_hoa || 0) : null,
        },
      };
      const r = await authFetch(`${API}/api/buyer/profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (r.ok) {
        if (onComplete) onComplete(body);
        else navigate('/discover');
      } else {
        const d = await r.json();
        setError(d.detail || 'Failed to save profile');
      }
    } catch { setError('Something went wrong. Please try again.'); }
    setSaving(false);
  };

  const StepComp = STEPS[step - 1];
  const isLast = step === TOTAL_STEPS;
  const progress = (step / TOTAL_STEPS) * 100;

  return (
    <div className="min-h-screen" style={{ background: '#F8FAFC' }}>
      {/* Sticky header */}
      <div className="sticky top-0 z-10 bg-white border-b border-slate-100 px-4 py-3">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-slate-400">Buyer Intake</span>
            <span className="text-xs font-bold" style={{ color: ACCENT }}>{step}/{TOTAL_STEPS}</span>
          </div>
          <div className="w-full bg-slate-100 rounded-full h-1.5">
            <div className="h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${progress}%`, background: ACCENT }} />
          </div>
          <div className="flex justify-between mt-1">
            {STEPS.map((_, i) => (
              <div key={i}
                className="w-2 h-2 rounded-full transition-all"
                style={{ background: i + 1 <= step ? ACCENT : '#E2E8F0' }} />
            ))}
          </div>
        </div>
      </div>

      {/* Step content */}
      <div className="max-w-lg mx-auto px-4 py-6 pb-28">
        <AnimatePresence mode="wait">
          <motion.div key={step}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.2 }}>
            <StepComp w={w} setW={setW} />
          </motion.div>
        </AnimatePresence>

        {error && (
          <div className="mt-4 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
            {error}
          </div>
        )}
      </div>

      {/* Fixed bottom navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-100 px-4 py-4 z-20">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          {step > 1 && (
            <button type="button" onClick={goBack}
              className="flex items-center gap-1.5 px-4 py-3 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-all">
              <ChevronLeft size={16} />Back
            </button>
          )}
          {!isLast ? (
            <button type="button" onClick={goNext} data-testid="wizard-next-btn"
              className="flex-1 flex items-center justify-center gap-1.5 py-3 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90"
              style={{ background: ACCENT }}>
              Continue <ChevronRight size={16} />
            </button>
          ) : (
            <button type="button" onClick={handleSave} disabled={saving} data-testid="wizard-save-btn"
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90 disabled:opacity-60"
              style={{ background: GREEN }}>
              <CheckCircle size={16} />
              {saving ? 'Saving...' : 'Save & Find My Matches'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
